import React, { FC, useCallback, useRef, useEffect, memo } from 'react';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';
import _isEmpty from 'lodash/isEmpty';
import _isEqualWith from 'lodash/isEqualWith';
import _isFunction from 'lodash/isFunction';
import { FormikContextType, useField, useFormikContext } from 'formik';
import { setFieldValueAndTouched } from '@minecraft.utils';
import { gqlProfileUnionFilter, gqlRole } from '@minecraft.graphql-types';
import {
  useGenderAppearance,
  useProfileUnionFilter,
  sagAftraOption,
  getEthnicAppearance,
  getPlayableAgeRange,
  getWorkingLocation,
  FilterData,
  FilterValues,
  FilterDrawerType,
  isEqualCustomizer,
} from '@blocs.filter';
import {
  QUERY_PROFILE_SEARCH_UNION_FILTER_BY_ROLE,
  QUERY_ROLE_DETAILS,
} from '../../FilterBasic/FilterBasicCollection/FilterBasicCollectionGraphQL';
import { StyledCheckbox } from '../../shared/styles';

const Wrapper = styled.div`
  background-color: ${(props): string => props.theme.colors.accent.ltGrey2};
  border: 0.0625px solid ${(props): string => props.theme.colors.primary.ltGrey1};
  border-radius: 0.25rem;
  padding: 1rem;
  position: relative;
  min-height: 3.375rem;

  ${StyledCheckbox} {
    margin: 0;
  }
`;

type Props = {
  autofillByRoleCriteriaName: FilterData['name'];
  unionStatusName?: FilterData['name'];
  excludeUnionStatusName?: FilterData['name'];
  genderName?: FilterData['name'];
  additionallyIdentifiesAsName?: FilterData['name'];
  ethnicAppearanceName?: FilterData['name'];
  switchAgeToMonthsName?: FilterData['name'];
  playableAgeRangeName?: FilterData['name'];
  playableAgeMinName?: FilterData['name'];
  playableAgeMaxName?: FilterData['name'];
  workingLocationName?: FilterData['name'];
  label: FilterData['label'];
  type?: FilterDrawerType;
  roleId: number;
  initialFilters: FilterValues;
  getAdditionalAutofillFilters?: (autofillByRoleCriteria?: boolean) => Partial<FilterValues>;
};

const AutofillByRoleCriteria: FC<Props> = ({
  autofillByRoleCriteriaName,
  unionStatusName,
  excludeUnionStatusName,
  genderName,
  additionallyIdentifiesAsName,
  ethnicAppearanceName,
  switchAgeToMonthsName,
  playableAgeRangeName,
  playableAgeMinName,
  playableAgeMaxName,
  workingLocationName,
  label,
  type,
  roleId,
  initialFilters,
  getAdditionalAutofillFilters,
}) => {
  const getGenderAppearance = useGenderAppearance();
  const getProfileUnionFilter = useProfileUnionFilter();

  const withUnionStatus = Boolean(unionStatusName) && Boolean(excludeUnionStatusName);
  const withGenderAppearance = Boolean(genderName && additionallyIdentifiesAsName);
  const withEthnicAppearance = Boolean(ethnicAppearanceName);
  const withPlayableAgeRange = Boolean(
    switchAgeToMonthsName && playableAgeRangeName && playableAgeMinName && playableAgeMaxName
  );
  const withWorkingLocation = Boolean(workingLocationName);
  const withGetAdditionalAutofillFilters = _isFunction(getAdditionalAutofillFilters);
  const skipProjectReleaseLocations = type === FilterDrawerType.Reps;

  const formFormik = useFormikContext();
  const [field] = useField(autofillByRoleCriteriaName);

  const prevFiltersByRoleCriteria = useRef({});

  const { data: dataProfileSearchUnionFilterByRole, loading: loadingProfileSearchUnionFilterByRole } = useQuery<{
    getProfileSearchUnionFilterByRole: gqlProfileUnionFilter[];
  }>(QUERY_PROFILE_SEARCH_UNION_FILTER_BY_ROLE, {
    variables: {
      roleId,
    },
    skip: !withUnionStatus,
  });

  const { data: dataRoleDetails, loading: loadingRoleDetails } = useQuery<{ roles: gqlRole[] }>(QUERY_ROLE_DETAILS, {
    variables: {
      roleId,
      withGenderAppearance,
      withEthnicAppearance,
      withPlayableAgeRange,
      withWorkingLocation,
      skipProjectReleaseLocations,
    },
  });

  const profileSearchUnionFilterByRole =
    dataProfileSearchUnionFilterByRole?.getProfileSearchUnionFilterByRole?.[0] || ({} as gqlProfileUnionFilter);
  const role = dataRoleDetails?.roles[0] || ({} as gqlRole);

  const toggleAutoFillByRoleCriteria = useCallback(
    (form: FormikContextType<FilterValues>): void => {
      const autofillByRoleCriteria = !form.values[autofillByRoleCriteriaName];

      if (autofillByRoleCriteria) {
        let filtersByRoleCriteria = { ...initialFilters, [autofillByRoleCriteriaName]: true };

        if (withUnionStatus && role?.displayedRoleUnion !== sagAftraOption.label) {
          const union = getProfileUnionFilter({
            unionStatusName,
            excludeUnionStatusName,
            profileSearchUnionFilterByRole,
          });

          filtersByRoleCriteria[unionStatusName] = union[unionStatusName];
          filtersByRoleCriteria[excludeUnionStatusName] = union[excludeUnionStatusName];
        }

        if (withGenderAppearance) {
          const genderAppearance = getGenderAppearance({
            genderName,
            additionallyIdentifiesAsName,
            roleDetails: role,
          });

          filtersByRoleCriteria[genderName] = genderAppearance;
        }

        if (withEthnicAppearance) {
          filtersByRoleCriteria[ethnicAppearanceName] = getEthnicAppearance({
            roleDetails: role,
          });
        }

        if (withPlayableAgeRange) {
          const playableAgeRange = getPlayableAgeRange({
            switchToName: switchAgeToMonthsName,
            ageMinName: playableAgeMinName,
            ageMaxName: playableAgeMaxName,
            roleDetails: role,
          });

          filtersByRoleCriteria[playableAgeRangeName] = {
            ...filtersByRoleCriteria[playableAgeRangeName],
            ...playableAgeRange,
          };
        }

        if (withWorkingLocation) {
          filtersByRoleCriteria[workingLocationName] = getWorkingLocation({
            roleDetails: role,
          });
        }

        if (withGetAdditionalAutofillFilters) {
          filtersByRoleCriteria = {
            ...filtersByRoleCriteria,
            ...getAdditionalAutofillFilters(autofillByRoleCriteria),
          };
        }

        form.setValues(filtersByRoleCriteria);

        prevFiltersByRoleCriteria.current = filtersByRoleCriteria;
      } else {
        form.setValues(initialFilters);

        prevFiltersByRoleCriteria.current = initialFilters;
      }
    },
    [profileSearchUnionFilterByRole, role]
  );

  const onValuesChange = useCallback(
    (form: FormikContextType<FilterValues>): void => {
      const isPrevFiltersByRoleCriteriaValueEmpty = _isEmpty(prevFiltersByRoleCriteria.current);

      if (isPrevFiltersByRoleCriteriaValueEmpty) {
        prevFiltersByRoleCriteria.current = form.values;
      }

      if (
        !isPrevFiltersByRoleCriteriaValueEmpty &&
        form.values[autofillByRoleCriteriaName] &&
        !_isEqualWith(prevFiltersByRoleCriteria.current, form.values, isEqualCustomizer)
      ) {
        setFieldValueAndTouched(autofillByRoleCriteriaName, false, form);
      }
    },
    [prevFiltersByRoleCriteria.current]
  );

  useEffect(() => {
    onValuesChange(formFormik);
  }, [formFormik.values]);

  return (
    <Wrapper>
      <StyledCheckbox
        {...field}
        isChecked={field.value}
        disabled={loadingProfileSearchUnionFilterByRole || loadingRoleDetails}
        label={label}
        onChange={(): void => {
          toggleAutoFillByRoleCriteria(formFormik);
        }}
      />
    </Wrapper>
  );
};

export default memo(AutofillByRoleCriteria);
